'use client'

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { 
  CookieConsent, 
  CookieConsentContextType 
} from '@/types/cookie-consent'
import { 
  getConsentPreferences, 
  saveConsentPreferences, 
  createDefaultConsent, 
  createAcceptAllConsent,
  loadGoogleAnalytics,
  loadMarketingScripts,
  removeTrackingScripts,
  // clearAllConsentData
} from '@/lib/cookie-utils'

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

interface CookieConsentProviderProps {
  children: ReactNode
  googleAnalyticsId?: string
}

export function CookieConsentProvider({ 
  children, 
  googleAnalyticsId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID 
}: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize consent state on mount
  useEffect(() => {
    try {
      const existingConsent = getConsentPreferences()
      
      // console.log('🍪 Cookie Consent Debug:', {
      //   existingConsent,
      //   hasExisting: !!existingConsent,
      //   willShowBanner: !existingConsent
      // })
      
      if (existingConsent) {
        setConsent(existingConsent)
        setShowBanner(false)
        loadScriptsBasedOnConsent(existingConsent)
      } else {
        setShowBanner(true)
      }
      
      setIsInitialized(true)
      
      // Add debug function to window in development
      // if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      //   (window as any).clearCookieConsent = () => {
      //     clearAllConsentData()
      //     window.location.reload()
      //   }
      //   console.log('🛠️ Debug: Run clearCookieConsent() to clear all consent data')
      // }
    } catch (error) {
      console.warn('Failed to initialize cookie consent:', error)
      // Fallback to showing banner if there's an error
      setShowBanner(true)
      setIsInitialized(true)
    }
  }, [])

  // Load scripts based on consent
  const loadScriptsBasedOnConsent = (consentData: CookieConsent) => {
    try {
      if (consentData.analytics && googleAnalyticsId) {
        loadGoogleAnalytics(googleAnalyticsId)
      }
      
      if (consentData.marketing) {
        loadMarketingScripts()
      }
    } catch (error) {
      console.warn('Failed to load tracking scripts:', error)
    }
  }

  const acceptAll = () => {
    try {
      const newConsent = createAcceptAllConsent()
      setConsent(newConsent)
      saveConsentPreferences(newConsent)
      setShowBanner(false)
      setShowPreferences(false)
      loadScriptsBasedOnConsent(newConsent)
    } catch (error) {
      console.error('Failed to accept all cookies:', error)
    }
  }

  const rejectAll = () => {
    try {
      const newConsent = createDefaultConsent()
      setConsent(newConsent)
      saveConsentPreferences(newConsent)
      setShowBanner(false)
      setShowPreferences(false)
      removeTrackingScripts()
    } catch (error) {
      console.error('Failed to reject all cookies:', error)
    }
  }

  const savePreferences = (preferences: Partial<CookieConsent>) => {
    try {
      const newConsent: CookieConsent = {
        essential: true, // Always true
        analytics: preferences.analytics ?? false,
        marketing: preferences.marketing ?? false,
        // preferences: preferences.preferences ?? false,
        timestamp: Date.now(),
        version: consent?.version || '1.0.0'
      }
      
      setConsent(newConsent)
      saveConsentPreferences(newConsent)
      setShowBanner(false)
      setShowPreferences(false)
      
      // Remove all scripts first, then load based on new consent
      removeTrackingScripts()
      setTimeout(() => {
        loadScriptsBasedOnConsent(newConsent)
      }, 100)
    } catch (error) {
      console.error('Failed to save cookie preferences:', error)
    }
  }

  const openPreferences = () => {
    setShowPreferences(true)
  }

  const closePreferences = () => {
    setShowPreferences(false)
    // Don't hide banner - user hasn't made a consent decision yet
  }

  const closeBanner = () => {
    setShowBanner(false)
  }

  const hasConsent = (category: keyof Omit<CookieConsent, 'timestamp' | 'version'>): boolean => {
    return consent?.[category] ?? false
  }

  const contextValue: CookieConsentContextType = {
    consent,
    showBanner: showBanner && isInitialized,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    closeBanner,
    hasConsent
  }

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent(): CookieConsentContextType {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}

// Hook for checking specific consent
export function useHasConsent(category: keyof Omit<CookieConsent, 'timestamp' | 'version'>): boolean {
  const { hasConsent } = useCookieConsent()
  return hasConsent(category)
}

// Hook for analytics tracking (only fires if consent given)
export function useAnalytics() {
  const { hasConsent } = useCookieConsent()
  
  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (!hasConsent('analytics')) return
    
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters)
    }
  }
  
  const trackPageView = (pagePath: string) => {
    if (!hasConsent('analytics')) return
    
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID, {
        page_path: pagePath
      })
    }
  }
  
  return { trackEvent, trackPageView, hasAnalyticsConsent: hasConsent('analytics') }
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void
  }
} 